import { colorFields, eyebrowField } from "./_shared";
/** Icon options shared with the value strip's icon set so the seller has a
 *  consistent, recognisable glyph library across sections. */
const ICON_OPTIONS = [
    { value: "truck", label: { ar: "شاحنة", en: "Truck" } },
    { value: "returns", label: { ar: "إرجاع", en: "Returns" } },
    { value: "shield", label: { ar: "درع", en: "Shield" } },
    { value: "whatsapp", label: { ar: "واتساب", en: "WhatsApp" } },
    { value: "clock", label: { ar: "ساعة", en: "Clock" } },
    { value: "star", label: { ar: "نجمة", en: "Star" } },
    { value: "gift", label: { ar: "هدية", en: "Gift" } },
    { value: "lock", label: { ar: "قفل", en: "Lock" } },
    { value: "headset", label: { ar: "دعم", en: "Support" } },
    { value: "tag", label: { ar: "وسم", en: "Tag" } },
];
/**
 * Feature row — a small "icon + headline + blurb" trio (or more) the seller
 * uses to highlight selling points (free shipping, authentic, easy returns…).
 * Seller-addable, repeatable `feature` blocks, five layout variants.
 */
export const featuresSchema = {
    type: "features",
    name: { ar: "صف الميزات", en: "Feature row" },
    description: {
        ar: "أيقونة وعنوان ووصف قصير — أبرز نقاط قوتك.",
        en: "Icon + headline + blurb — highlight your selling points.",
    },
    maxPerTemplate: 4,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: {
                ar: "طريقة عرض الميزات.",
                en: "How the features are laid out.",
            },
            options: [
                { value: "feat-grid", label: { ar: "شبكة", en: "Grid" } },
                { value: "feat-cards", label: { ar: "بطاقات", en: "Cards" } },
                { value: "feat-list", label: { ar: "قائمة", en: "List" } },
                { value: "feat-center", label: { ar: "متوسط", en: "Centered" } },
                { value: "feat-compact", label: { ar: "مدمج", en: "Compact" } },
                /**
                 * Ported for the `pearl` family (2026-07-20) from their
                 * `trust-promise` block. A VARIANT rather than a section type: the
                 * content model is icon + title + blurb, field for field what every
                 * other layout here reads.
                 *
                 * The near-neighbour is `feat-list` — both set the icon BESIDE the
                 * text in an auto-fit grid — and the separation is measured in the
                 * box, the same standard `count-strip` and `count-flash` were
                 * separated by:
                 *
                 *  - the MEDALLION. `feat-list` boxes the icon in a 46px rounded
                 *    SQUARE (radius 12) on a neutral `--paper-2`. This is a 44px
                 *    CIRCLE tinted from the store accent — `color-mix(accent 16%)`
                 *    fill inside a `color-mix(accent 35%)` ring. That medallion is
                 *    the block's whole visual signature.
                 *  - the BOX. MEASURED 130px at 1200/1440 on pearl-bags against the
                 *    ~215px a `feat-list` section occupies at the same content, because
                 *    this is a 28px-padded RULE BAND between two sections rather than a
                 *    64px-padded content block. It carries a hairline on both edges.
                 *  - the HEADING. It never prints one; their markup has no title
                 *    element at all, and the row is capped at 1100px and centred.
                 */
                { value: "feat-promise", label: { ar: "شريط الضمانات", en: "Promise bar" } },
            ],
            default: "feat-grid",
        },
        ...eyebrowField(),
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان (اختياري)", en: "Title (optional)" },
            validate: { max: 60 },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        ...colorFields(),
    ],
    blocks: [
        {
            type: "feature",
            name: { ar: "ميزة", en: "Feature" },
            description: { ar: "أيقونة وعنوان ووصف.", en: "Icon, title and blurb." },
            maxPerSection: 6,
            settings: [
                {
                    id: "icon",
                    type: "select",
                    ui: "icon",
                    label: { ar: "الأيقونة", en: "Icon" },
                    options: ICON_OPTIONS,
                    default: "star",
                },
                {
                    id: "title",
                    type: "text",
                    i18n: true,
                    label: { ar: "العنوان", en: "Title" },
                    validate: { max: 40 },
                },
                {
                    id: "description",
                    type: "text",
                    i18n: true,
                    label: { ar: "الوصف", en: "Description" },
                    // C5: `feat-compact` renders icon + title on ONE nowrap line and never
                    // reads `description` (`FeatureRowSection.tsx`, `layout === 'compact'`).
                    // A block field's `showIf` is scoped to the BLOCK's own settings, so it
                    // can't gate on the section's `variant` — and hiding authored copy would
                    // cost the seller access to words they wrote. Hence a hint.
                    helpText: {
                        ar: "بيظهر في كل الأشكال ما عدا «مدمج» — المدمج سطر واحد فيه الأيقونة والعنوان بس.",
                        en: "Shown in every layout except Compact, which is a single line of icon + title.",
                    },
                    validate: { max: 120 },
                },
            ],
        },
    ],
};
//# sourceMappingURL=features.js.map